import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import api from '../api';
import {
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  Container,
  Alert,
  LinearProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloseIcon from '@mui/icons-material/Close';

const PRIMARY = '#6366f1';

function UploadPage() {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleFiles = (newFiles) => {
    const validTypes = [
      'text/csv',
      'application/pdf',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];
    const validExtensions = ['.csv', '.pdf', '.xls', '.xlsx'];

    const validFiles = [];
    const invalidFiles = [];

    newFiles.forEach((file) => {
      const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
      if (validTypes.includes(file.type) || validExtensions.includes(fileExtension)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length > 0) {
      setError(
        `Invalid file types: ${invalidFiles.join(', ')}. Please upload CSV, Excel, or PDF files only.`
      );
    } else {
      setError(null);
    }

    setFiles((prevFiles) => [...prevFiles, ...validFiles]);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFiles,
    accept: {
      'text/csv': ['.csv'],
      'application/pdf': ['.pdf'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    multiple: true,
    noClick: false,
  });

  const removeFile = (index) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setError('Please select at least one file');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });

      const response = await api.post('/api/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      navigate('/review', {
        state: {
          transactions: response.data.transactions,
          duplicates: response.data.duplicates,
          fileResults: response.data.fileResults,
          filesProcessed: response.data.filesProcessed,
        },
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed. Please try again.');
      setUploading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: 'var(--bg-surf)', minHeight: '100vh', py: 6 }}>
      <Container maxWidth="sm">
        <Typography variant="h5" fontWeight={700} mb={0.5} color="var(--tx-hi)">
          Upload Credit Card Statements
        </Typography>
        <Typography variant="body2" color="var(--tx-lo)" mb={4}>
          Upload one or more CSV, Excel, or PDF credit card statements
        </Typography>

        {/* Dropzone */}
        <Paper
          {...getRootProps()}
          elevation={0}
          sx={{
            border: '2px dashed',
            borderColor: isDragActive ? PRIMARY : 'rgba(99,102,241,0.5)',
            borderRadius: 3,
            p: 6,
            textAlign: 'center',
            cursor: 'pointer',
            bgcolor: isDragActive ? 'rgba(99,102,241,0.1)' : '#151e2e',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: PRIMARY,
              bgcolor: 'rgba(99,102,241,0.1)',
            },
          }}
        >
          <input {...getInputProps()} />
          <CloudUploadIcon sx={{ fontSize: 56, color: isDragActive ? PRIMARY : '#a5b4fc', mb: 2 }} />
          <Typography variant="body1" fontWeight={500} color={isDragActive ? PRIMARY : 'var(--tx-hi)'} mb={0.5}>
            {files.length > 0
              ? `${files.length} file${files.length > 1 ? 's' : ''} selected`
              : 'Drag and drop your files here, or click to browse'}
          </Typography>
          <Typography variant="caption" color="var(--tx-lo)">
            Supports CSV, Excel (.xls, .xlsx), and PDF files (max 10MB each)
          </Typography>
        </Paper>

        {/* File List */}
        {files.length > 0 && (
          <Paper
            elevation={0}
            sx={{ mt: 3, p: 2, border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2, bgcolor: '#151e2e' }}
          >
            <Typography variant="subtitle2" fontWeight={600} mb={1.5} color="var(--tx-hi)">
              Selected Files
            </Typography>
            <Stack gap={1}>
              {files.map((file, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'rgba(255,255,255,0.08)',
                    bgcolor: 'rgba(255,255,255,0.04)',
                    '&:hover': { borderColor: PRIMARY },
                    transition: 'border-color 0.2s',
                  }}
                >
                  <InsertDriveFileIcon sx={{ color: PRIMARY, fontSize: 20, flexShrink: 0 }} />
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="var(--tx-lo)" sx={{ flexShrink: 0 }}>
                    {(file.size / 1024).toFixed(1)} KB
                  </Typography>
                  <Tooltip title="Remove">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                      disabled={uploading}
                      sx={{ color: 'error.main', flexShrink: 0 }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Stack>
          </Paper>
        )}

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Upload Button */}
        {files.length > 0 && !uploading && (
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleUpload}
            sx={{
              mt: 3,
              py: 1.5,
              bgcolor: PRIMARY,
              fontWeight: 600,
              fontSize: 16,
              borderRadius: 2,
              '&:hover': { bgcolor: '#4f46e5' },
            }}
          >
            Process {files.length} File{files.length > 1 ? 's' : ''}
          </Button>
        )}

        {/* Uploading State */}
        {uploading && (
          <Box mt={4} textAlign="center">
            <LinearProgress
              sx={{
                mb: 2,
                borderRadius: 1,
                '& .MuiLinearProgress-bar': { bgcolor: PRIMARY },
                bgcolor: 'rgba(99,102,241,0.15)',
              }}
            />
            <Typography variant="body1" fontWeight={500} color="var(--tx-hi)" mb={0.5}>
              Processing {files.length} file{files.length > 1 ? 's' : ''}...
            </Typography>
            <Typography variant="caption" color="var(--tx-lo)">
              This may take {files.length * 30}–{files.length * 60} seconds
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
}

export default UploadPage;
